import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBoxOpen,
  FaDownload,
  FaFileAlt,
  FaPaperPlane,
  FaPlus,
  FaSync,
  FaUpload,
} from "react-icons/fa";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";
import {
  CUSTOM_DESIGN_API_BASE,
  eventTypeTranslationKeys,
  projectStatusTranslationKeys,
  quantityTranslationKeys,
  quoteStatusTranslationKeys,
  quoteTypeTranslationKeys,
  serviceTypeTranslationKeys,
  versionStatusTranslationKeys,
} from "./customDesignUtils";
import "./AdminCustomProjects.css";

const statuses = [
  "submitted", "feasibility_review", "design_payment_pending", "modeling",
  "design_review", "prototype_payment_pending", "prototyping", "prototype_shipped",
  "sample_review", "production_payment_pending", "in_production", "completed", "cancelled",
];

const emptyQuote = {
  quoteType: "design",
  notes: "",
  leadTime: "",
  validUntil: "",
  collectShipping: false,
  items: [{ description: "", quantity: 1, amount: "" }],
};

const apiErrorKeys = {
  400: "admin.projects.error.validation",
  401: "admin.loginRequired",
  403: "admin.projects.error.forbidden",
  404: "admin.projects.error.notFound",
  409: "admin.projects.error.conflict",
  413: "admin.projects.error.tooLarge",
  429: "admin.projects.error.rateLimit",
  503: "admin.projects.error.unavailable",
};

const fileKindKeys = {
  drawing: "admin.projects.fileKind.drawing",
  cad: "admin.projects.fileKind.cad",
  reference: "admin.projects.fileKind.reference",
  model: "admin.projects.fileKind.model",
  render: "admin.projects.fileKind.render",
  message_attachment: "admin.projects.fileKind.attachment",
};

const notificationStatusKeys = {
  pending: "admin.projects.notification.pending",
  sent: "admin.projects.notification.sent",
  failed: "admin.projects.notification.failed",
};

const projectEventKeys = {
  ...eventTypeTranslationKeys,
  files_uploaded: "admin.projects.event.filesUploaded",
  design_approved: "admin.projects.event.designApproved",
  design_changes_requested: "admin.projects.event.designChangesRequested",
  design_reviewed: "admin.projects.event.designReviewed",
  checkout_started: "admin.projects.event.checkoutStarted",
  design_version_published: "admin.projects.event.designVersionPublished",
};

const translateDesignValue = (t, keys, value) => t(
  Object.prototype.hasOwnProperty.call(keys, value) ? keys[value] : "design.enum.unknown",
);

const AdminCustomProjects = () => {
  const { t, formatDate, formatNumber, formatRegion } = useI18n();
  const [auth, setAuth] = useState(localStorage.getItem("adminAuth") || "");
  const [login, setLogin] = useState({ username: "", password: "" });
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [messageFile, setMessageFile] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [quote, setQuote] = useState(emptyQuote);
  const [version, setVersion] = useState({ title: "", notes: "", glb: null, renders: [] });
  const [shipping, setShipping] = useState({ trackingCarrier: "Yanwen", trackingNumber: "", trackingUrl: "", shippingNote: "" });
  const [storage, setStorage] = useState(null);

  const headers = useMemo(() => (auth ? { Authorization: `Basic ${auth}` } : {}), [auth]);
  const projectDate = (value) => value ? formatDate(value, { dateStyle: "medium", timeStyle: "short" }) : "";
  const usd = (cents) => formatNumber(Number(cents || 0) / 100, { style: "currency", currency: "USD" });

  const request = useCallback(async (path, options = {}) => {
    const response = await fetch(`${CUSTOM_DESIGN_API_BASE}${path}`, {
      ...options,
      headers: { ...headers, ...(options.headers || {}) },
    });
    if (!response.ok) {
      const error = new Error();
      error.status = response.status;
      throw error;
    }
    return response.json();
  }, [headers]);

  const loadProjects = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    setStatusMessage("");
    try {
      const data = await request("/api/admin/custom-design/projects");
      const items = Array.isArray(data.items) ? data.items : [];
      setProjects(items);
      setStorage(data.storage || null);
      setSelectedId((current) => current || items[0]?.id || "");
    } catch (error) {
      setStatusMessage(apiErrorKeys[error.status] || "admin.projects.error.loadProjects");
      if (error.status === 401) {
        localStorage.removeItem("adminAuth");
        setAuth("");
      }
    } finally {
      setLoading(false);
    }
  }, [auth, request]);

  const loadDetail = useCallback(async (id) => {
    if (!auth || !id) return;
    setDetailLoading(true);
    try {
      const data = await request(`/api/admin/custom-design/projects/${id}`);
      setProject(data.project);
      setShipping({
        trackingCarrier: data.project.trackingCarrier || "Yanwen",
        trackingNumber: data.project.trackingNumber || "",
        trackingUrl: data.project.trackingUrl || "",
        shippingNote: data.project.shippingNote || "",
      });
    } catch (error) {
      setStatusMessage(apiErrorKeys[error.status] || "admin.projects.error.loadProject");
    } finally {
      setDetailLoading(false);
    }
  }, [auth, request]);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const syncProject = (updated) => {
    setProject(updated);
    setProjects((current) => current.map((item) => (
      item.id === updated.id ? { ...item, ...updated } : item
    )));
  };

  const handleLogin = (event) => {
    event.preventDefault();
    const encoded = window.btoa(`${login.username}:${login.password}`);
    localStorage.setItem("adminAuth", encoded);
    setAuth(encoded);
    setLogin({ username: "", password: "" });
  };

  const runAction = async (name, action, successMessage) => {
    setBusy(name);
    setStatusMessage("");
    try {
      const data = await action();
      if (data.project) syncProject(data.project);
      setStatusMessage(successMessage);
      return data;
    } catch (error) {
      setStatusMessage(apiErrorKeys[error.status] || "admin.projects.error.action");
      return null;
    } finally {
      setBusy("");
    }
  };

  const updateStatus = (status) => runAction(
    "status",
    () => request(`/api/admin/custom-design/projects/${project.id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    }),
    "admin.projects.success.status",
  );

  const updateQuoteItem = (index, key, value) => {
    setQuote((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [key]: value } : item
      )),
    }));
  };

  const createQuote = async (event) => {
    event.preventDefault();
    const payload = {
      ...quote,
      validUntil: quote.validUntil ? new Date(quote.validUntil).toISOString() : "",
      items: quote.items.map((item) => ({
        description: item.description,
        quantity: Number(item.quantity),
        unitAmountCents: Math.round(Number(item.amount) * 100),
      })),
    };
    const data = await runAction(
      "quote",
      () => request(`/api/admin/custom-design/projects/${project.id}/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }),
      "admin.projects.success.quoteDraft",
    );
    if (data) setQuote(emptyQuote);
  };

  const publishQuote = (quoteId) => runAction(
    `publish-${quoteId}`,
    () => request(`/api/admin/custom-design/quotes/${quoteId}/publish`, { method: "POST" }),
    "admin.projects.success.quotePublished",
  );

  const cancelQuote = (quoteId) => runAction(
    `cancel-${quoteId}`,
    () => request(`/api/admin/custom-design/quotes/${quoteId}/cancel`, { method: "POST" }),
    "admin.projects.success.quoteCancelled",
  );

  const uploadVersion = async (event) => {
    event.preventDefault();
    const payload = new FormData();
    payload.append("title", version.title);
    payload.append("notes", version.notes);
    if (version.glb) payload.append("glb", version.glb);
    version.renders.forEach((file) => payload.append("renders", file));
    const data = await runAction(
      "version",
      () => request(`/api/admin/custom-design/projects/${project.id}/versions`, {
        method: "POST",
        body: payload,
      }),
      "admin.projects.success.version",
    );
    if (data) setVersion({ title: "", notes: "", glb: null, renders: [] });
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    const payload = new FormData();
    payload.append("body", message);
    if (messageFile) payload.append("file", messageFile);
    const data = await runAction(
      "message",
      () => request(`/api/admin/custom-design/projects/${project.id}/messages`, {
        method: "POST",
        body: payload,
      }),
      "admin.projects.success.message",
    );
    if (data) {
      setMessage("");
      setMessageFile(null);
    }
  };

  const updateShipping = async (event) => {
    event.preventDefault();
    await runAction(
      "shipping",
      () => request(`/api/admin/custom-design/projects/${project.id}/shipping`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(shipping),
      }),
      "admin.projects.success.shipping",
    );
  };

  const retryNotification = (notificationId) => runAction(
    `notification-${notificationId}`,
    () => request(`/api/admin/custom-design/notifications/${notificationId}/retry`, { method: "POST" }),
    "admin.projects.success.notification",
  );

  const downloadFile = async (file) => {
    setBusy(`file-${file.id}`);
    try {
      const response = await fetch(`${CUSTOM_DESIGN_API_BASE}${file.url}`, { headers });
      if (!response.ok) {
        const error = new Error();
        error.status = response.status;
        throw error;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = file.originalName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setStatusMessage(apiErrorKeys[error.status] || "admin.projects.error.download");
    } finally {
      setBusy("");
    }
  };

  return (
    <main className="admin-custom-projects">
      <Seo title={t("admin.projects.seo.title")} description={t("admin.projects.seo.description")} path="/admin/custom-projects" noindex />
      <header className="admin-custom-projects__header">
        <div>
          <h1>{t("admin.projects.title")}</h1>
          <nav><Link to="/admin">{t("admin.nav.products")}</Link><Link to="/admin/orders">{t("admin.nav.orders")}</Link><Link to="/admin/custom-requests">{t("admin.nav.requests")}</Link><Link to="/admin/users">{t("admin.nav.users")}</Link></nav>
        </div>
        {auth ? <button type="button" onClick={() => { localStorage.removeItem("adminAuth"); setAuth(""); }}>{t("admin.logout")}</button> : null}
      </header>
      {statusMessage && <div className="admin-project-message" role="status">{t(statusMessage)}</div>}

      {!auth ? (
        <form className="admin-project-login" onSubmit={handleLogin}>
          <h2>{t("admin.loginTitle")}</h2>
          <label>{t("admin.username")}<input type="text" value={login.username} onChange={(event) => setLogin((current) => ({ ...current, username: event.target.value }))} required /></label>
          <label>{t("admin.password")}<input type="password" value={login.password} onChange={(event) => setLogin((current) => ({ ...current, password: event.target.value }))} required /></label>
          <button type="submit">{t("admin.login")}</button>
        </form>
      ) : (
        <>
          {storage?.usedPercent >= 80 ? (
            <div className="admin-project-storage-alert">
              {t("admin.projects.storageAlert", { percent: formatNumber(storage.usedPercent / 100, { style: "percent", maximumFractionDigits: 1 }), space: formatNumber(storage.freeBytes / 1024 / 1024 / 1024, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}
            </div>
          ) : null}
          <div className="admin-custom-projects__layout">
            <aside className="admin-project-list">
              <header><h2>{t("admin.nav.projects")}</h2><button type="button" onClick={loadProjects} disabled={loading}><FaSync /> {t(loading ? "admin.loading" : "admin.refresh")}</button></header>
              {projects.map((item) => (
                <button className={selectedId === item.id ? "selected" : ""} type="button" key={item.id} onClick={() => setSelectedId(item.id)}>
                  <strong>{item.title}</strong><span>{item.customerName || item.customerEmail}</span>
                  <em>{translateDesignValue(t, projectStatusTranslationKeys, item.status)}</em><small>{projectDate(item.updatedAt)}</small>
                </button>
              ))}
              {!projects.length && !loading ? <p>{t("admin.projects.empty")}</p> : null}
            </aside>

            <section className="admin-project-detail">
              {detailLoading ? <p>{t("admin.projects.loadingDetail")}</p> : null}
              {!detailLoading && project ? (
                <>
                  <div className="admin-project-title">
                    <div><span>{project.id}</span><h2>{project.title}</h2><p>{project.description}</p></div>
                    <label>{t("admin.status")}<select value={project.status} onChange={(event) => updateStatus(event.target.value)} disabled={busy === "status"}>{statuses.map((status) => <option key={status} value={status}>{translateDesignValue(t, projectStatusTranslationKeys, status)}</option>)}</select></label>
                  </div>

                  <div className="admin-project-facts">
                    <div><span>{t("admin.customer")}</span><strong>{project.customerName || t("admin.notSet")}</strong><a href={`mailto:${project.customerEmail}`}>{project.customerEmail}</a></div>
                    <div><span>{t("design.detail.service")}</span><strong>{translateDesignValue(t, serviceTypeTranslationKeys, project.serviceType)}</strong></div>
                    <div><span>{t("admin.projects.country")}</span><strong>{project.countryCode ? formatRegion(project.countryCode) : t("admin.notSet")}</strong></div>
                    <div><span>{t("admin.quantity")}</span><strong>{Object.prototype.hasOwnProperty.call(quantityTranslationKeys, project.quantity) ? t(quantityTranslationKeys[project.quantity]) : project.quantity ? (Number.isFinite(Number(project.quantity)) ? formatNumber(Number(project.quantity)) : project.quantity) : t("admin.notSet")}</strong></div>
                    <div><span>{t("design.detail.dimensions")}</span><strong>{project.dimensions || t("admin.notSet")}</strong></div>
                    <div><span>{t("design.detail.material")}</span><strong>{project.material || t("design.detail.recommendation")}</strong></div>
                  </div>

                  <section className="admin-project-panel">
                    <h3>{t("admin.projects.customerFiles")}</h3>
                    <div className="admin-project-files">
                      {project.files.map((file) => (
                        <button type="button" onClick={() => downloadFile(file)} disabled={busy === `file-${file.id}`} key={file.id}>
                          <FaFileAlt /><span><strong>{file.originalName}</strong><small>{t("admin.projects.fileDetails", { size: formatNumber(file.sizeBytes / 1024 / 1024, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), kind: translateDesignValue(t, fileKindKeys, file.kind) })}</small></span><FaDownload />
                        </button>
                      ))}
                    </div>
                  </section>

                  <section className="admin-project-panel">
                    <div className="admin-panel-heading"><h3>{t("admin.projects.quotes")}</h3><span>{t("admin.projects.quotesImmutable")}</span></div>
                    <div className="admin-existing-quotes">
                      {project.quotes.map((item) => (
                        <article key={item.id}>
                          <header><div><strong>{translateDesignValue(t, quoteTypeTranslationKeys, item.quoteType)}</strong><span>{t("design.detail.version", { number: formatNumber(item.versionNumber) })}</span></div><em>{translateDesignValue(t, quoteStatusTranslationKeys, item.status)}</em></header>
                          <h4>{usd(item.totalCents)}</h4>
                          <ul>{item.items.map((line) => <li key={line.id}><span>{line.description} × {formatNumber(line.quantity)}</span><strong>{usd(line.unitAmountCents * line.quantity)}</strong></li>)}</ul>
                          <div>
                            {item.status === "draft" && <button type="button" onClick={() => publishQuote(item.id)} disabled={Boolean(busy)}>{t("admin.projects.publishQuote")}</button>}
                            {!["paid", "cancelled", "expired"].includes(item.status) && <button className="secondary" type="button" onClick={() => cancelQuote(item.id)} disabled={Boolean(busy)}>{t("admin.cancel")}</button>}
                          </div>
                        </article>
                      ))}
                    </div>
                    <form className="admin-quote-form" onSubmit={createQuote}>
                      <h4>{t("admin.projects.createQuote")}</h4>
                      <div className="admin-form-grid">
                        <label>{t("admin.projects.quoteType")}<select value={quote.quoteType} onChange={(event) => setQuote((current) => ({ ...current, quoteType: event.target.value }))}>{Object.entries(quoteTypeTranslationKeys).map(([type, key]) => <option key={type} value={type}>{t(key)}</option>)}</select></label>
                        <label>{t("admin.projects.leadTime")}<input type="text" value={quote.leadTime} onChange={(event) => setQuote((current) => ({ ...current, leadTime: event.target.value }))} placeholder={t("admin.projects.leadTimePlaceholder")} /></label>
                        <label>{t("admin.projects.validUntil")}<input type="datetime-local" value={quote.validUntil} onChange={(event) => setQuote((current) => ({ ...current, validUntil: event.target.value }))} /></label>
                        <label className="checkbox"><input type="checkbox" checked={quote.collectShipping} onChange={(event) => setQuote((current) => ({ ...current, collectShipping: event.target.checked }))} /> {t("admin.projects.collectShipping")}</label>
                      </div>
                      <div className="admin-quote-items">
                        {quote.items.map((item, index) => (
                          <div key={index}>
                            <input aria-label={t("admin.projects.description")} type="text" value={item.description} onChange={(event) => updateQuoteItem(index, "description", event.target.value)} placeholder={t("admin.projects.lineDescription")} required />
                            <input aria-label={t("admin.quantity")} type="number" min="1" value={item.quantity} onChange={(event) => updateQuoteItem(index, "quantity", event.target.value)} required />
                            <input aria-label={t("admin.projects.unitPrice")} type="number" min="0.5" step="0.01" value={item.amount} onChange={(event) => updateQuoteItem(index, "amount", event.target.value)} placeholder={t("admin.projects.unitPricePlaceholder")} required />
                            {quote.items.length > 1 && <button type="button" onClick={() => setQuote((current) => ({ ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) }))}>{t("admin.remove")}</button>}
                          </div>
                        ))}
                      </div>
                      <button className="admin-inline-action" type="button" onClick={() => setQuote((current) => ({ ...current, items: [...current.items, { description: "", quantity: 1, amount: "" }] }))}><FaPlus /> {t("admin.projects.addLineItem")}</button>
                      <label>{t("admin.projects.quoteNotes")}<textarea value={quote.notes} onChange={(event) => setQuote((current) => ({ ...current, notes: event.target.value }))} /></label>
                      <button type="submit" disabled={busy === "quote"}>{t(busy === "quote" ? "admin.projects.creating" : "admin.projects.createQuote")}</button>
                    </form>
                  </section>

                  <section className="admin-project-panel">
                    <div className="admin-panel-heading"><h3>{t("admin.projects.versions")}</h3><span>{t("admin.projects.versionFiles", { count: formatNumber(8) })}</span></div>
                    <div className="admin-version-list">
                      {project.versions.map((item) => (
                        <article key={item.id}>
                          <div><strong>{t("admin.projects.versionTitle", { number: formatNumber(item.versionNumber), title: item.title })}</strong><em>{translateDesignValue(t, versionStatusTranslationKeys, item.status)}</em></div>
                          <p>{item.notes}</p>
                          {item.customerFeedback && <blockquote>{item.customerFeedback}</blockquote>}
                          <div className="admin-project-files">
                            {[item.glbFile, ...(item.renders || [])].filter(Boolean).map((file) => (
                              <button type="button" onClick={() => downloadFile(file)} key={file.id}><FaDownload /> {file.originalName}</button>
                            ))}
                          </div>
                        </article>
                      ))}
                    </div>
                    <form className="admin-version-form" onSubmit={uploadVersion}>
                      <h4>{t("admin.projects.newVersion")}</h4>
                      <label>{t("admin.projects.versionName")}<input type="text" value={version.title} onChange={(event) => setVersion((current) => ({ ...current, title: event.target.value }))} placeholder={t("admin.projects.versionPlaceholder")} required /></label>
                      <label>{t("admin.projects.designNotes")}<textarea value={version.notes} onChange={(event) => setVersion((current) => ({ ...current, notes: event.target.value }))} /></label>
                      <label className="admin-file-picker"><FaUpload /> {version.glb ? version.glb.name : t("admin.projects.chooseModel")}<input type="file" accept=".glb" onChange={(event) => setVersion((current) => ({ ...current, glb: event.target.files?.[0] || null }))} required /></label>
                      <label className="admin-file-picker"><FaUpload /> {version.renders.length ? t("admin.projects.rendersSelected", { count: formatNumber(version.renders.length) }) : t("admin.projects.chooseRenders")}<input type="file" multiple accept=".png,.jpg,.jpeg,.webp" onChange={(event) => setVersion((current) => ({ ...current, renders: Array.from(event.target.files || []) }))} /></label>
                      <button type="submit" disabled={busy === "version"}>{t(busy === "version" ? "admin.projects.uploading" : "admin.projects.publishVersion")}</button>
                    </form>
                  </section>

                  <section className="admin-project-panel">
                    <h3>{t("admin.projects.conversation")}</h3>
                    <div className="admin-message-list">
                      {project.messages.map((item) => <article key={item.id}><header><strong>{item.authorRole === "admin" ? t("design.detail.designTeam") : item.authorName || t("admin.customer")}</strong><span>{projectDate(item.createdAt)}</span></header><p>{item.body}</p></article>)}
                    </div>
                    <form className="admin-message-form" onSubmit={sendMessage}>
                      <textarea value={message} onChange={(event) => setMessage(event.target.value)} placeholder={t("admin.projects.messagePlaceholder")} required />
                      <div><label className="admin-file-picker"><FaFileAlt /> {messageFile ? messageFile.name : t("design.detail.attach")}<input type="file" accept=".png,.jpg,.jpeg,.webp,.pdf,.step,.stp,.dxf" onChange={(event) => setMessageFile(event.target.files?.[0] || null)} /></label><button type="submit" disabled={busy === "message"}><FaPaperPlane /> {t(busy === "message" ? "design.detail.sending" : "design.detail.send")}</button></div>
                    </form>
                  </section>

                  <section className="admin-project-panel">
                    <h3>{t("admin.projects.shipping")}</h3>
                    <form className="admin-form-grid" onSubmit={updateShipping}>
                      <label>{t("admin.projects.carrier")}<input type="text" value={shipping.trackingCarrier} onChange={(event) => setShipping((current) => ({ ...current, trackingCarrier: event.target.value }))} /></label>
                      <label>{t("admin.projects.trackingNumber")}<input type="text" value={shipping.trackingNumber} onChange={(event) => setShipping((current) => ({ ...current, trackingNumber: event.target.value }))} required /></label>
                      <label>{t("admin.projects.trackingUrl")}<input type="url" value={shipping.trackingUrl} onChange={(event) => setShipping((current) => ({ ...current, trackingUrl: event.target.value }))} /></label>
                      <label>{t("admin.projects.shippingNote")}<input type="text" value={shipping.shippingNote} onChange={(event) => setShipping((current) => ({ ...current, shippingNote: event.target.value }))} /></label>
                      <button type="submit" disabled={busy === "shipping"}><FaBoxOpen /> {t(busy === "shipping" ? "admin.saving" : "admin.projects.publishShipment")}</button>
                    </form>
                  </section>

                  <section className="admin-project-panel">
                    <h3>{t("admin.projects.notifications")}</h3>
                    <div className="admin-notifications">
                      {project.notifications.map((item) => (
                        <article key={item.id}><div><strong>{t("admin.projects.notificationTitle", { channel: item.channel === "wework" ? t("admin.requests.wework") : item.channel === "email" ? t("admin.projects.email") : t("admin.projects.unknownChannel"), subject: translateDesignValue(t, projectEventKeys, item.eventType) })}</strong><span>{t("admin.projects.notificationAttempts", { status: translateDesignValue(t, notificationStatusKeys, item.status), count: formatNumber(item.attempts) })}</span>{item.error && <small>{t("admin.projects.notificationError")}</small>}</div>{item.status !== "sent" && <button type="button" onClick={() => retryNotification(item.id)} disabled={Boolean(busy)}>{t("admin.projects.retry")}</button>}</article>
                      ))}
                    </div>
                    <ol className="admin-timeline">
                      {[...project.events].reverse().map((item) => <li key={item.id}><strong>{translateDesignValue(t, projectEventKeys, item.eventType)}</strong><span>{projectDate(item.createdAt)}</span></li>)}
                    </ol>
                  </section>
                </>
              ) : null}
            </section>
          </div>
        </>
      )}
    </main>
  );
};

export default AdminCustomProjects;
